import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PRODUCTS } from './Categories.jsx';
import { addItem } from '../store/cart.js';

const COLORS = { black: '#0A0A0A', white: '#F5F5F0', gold: '#C9A84C', gray: '#888' };

const SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

// TODO(M2): Only show sizes relevant to product category (caps/totes have no size)
const PRODUCTS_WITH_SIZES = ['qr-tee', 'qr-hoodie'];

function QRCodePreview({ productName }) {
  return (
    <div style={{
      width: '100%',
      aspectRatio: '1',
      maxWidth: '480px',
      border: `1px solid rgba(255,255,255,0.12)`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '1.5rem',
      padding: '3rem',
      backgroundColor: 'rgba(255,255,255,0.015)',
    }}>
      {/* QR stand-in — inner grid */}
      <div style={{
        width: '220px',
        height: '220px',
        border: `2px solid ${COLORS.white}`,
        padding: '12px',
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gridTemplateRows: 'repeat(7, 1fr)',
        gap: '2px',
      }}>
        {Array.from({ length: 49 }).map((_, i) => {
          const row = Math.floor(i / 7);
          const col = i % 7;
          // Simulate finder patterns in three corners
          const topLeft = row < 3 && col < 3;
          const topRight = row < 3 && col >= 4;
          const bottomLeft = row >= 4 && col < 3;
          const isRandom = Math.sin(i * 13.7 + 2.1) > 0.15;
          const filled = topLeft || topRight || bottomLeft || isRandom;
          return (
            <div
              key={i}
              style={{ backgroundColor: filled ? COLORS.white : 'transparent' }}
            />
          );
        })}
      </div>

      <div style={{ textAlign: 'center' }}>
        <p style={{
          fontSize: '0.65rem',
          letterSpacing: '0.2em',
          color: COLORS.gold,
          margin: '0 0 0.25rem',
          fontWeight: 600,
        }}>
          YOUR CODE — PLACEHOLDER
        </p>
        <p style={{
          fontSize: '0.72rem',
          color: 'rgba(255,255,255,0.35)',
          margin: 0,
          letterSpacing: '0.08em',
        }}>
          Unique code assigned at checkout
        </p>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  const [selectedSize, setSelectedSize] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);

  if (!product) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        color: COLORS.white,
        fontFamily: 'Inter, system-ui, sans-serif',
      }}>
        <p style={{ opacity: 0.5 }}>Product not found.</p>
        <Link to="/shop" style={{ color: COLORS.gold, textDecoration: 'none', letterSpacing: '0.1em', fontSize: '0.8rem' }}>
          BACK TO SHOP
        </Link>
      </div>
    );
  }

  const needsSize = PRODUCTS_WITH_SIZES.includes(product.slug);

  function handleAddToCart() {
    if (needsSize && !selectedSize) return;
    addItem(product, selectedSize);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
      {/* Breadcrumb */}
      <nav style={{ marginBottom: '3rem' }}>
        <Link to="/shop" style={{
          fontSize: '0.7rem',
          letterSpacing: '0.15em',
          color: COLORS.gray,
          textDecoration: 'none',
        }}>
          SHOP
        </Link>
        <span style={{ color: COLORS.gray, margin: '0 0.75rem', opacity: 0.4 }}>→</span>
        <span style={{ fontSize: '0.7rem', letterSpacing: '0.15em', color: COLORS.white }}>
          {product.name}
        </span>
      </nav>

      {/* Product layout: 2 columns */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '5rem',
        alignItems: 'start',
      }}>
        {/* Left: QR preview */}
        <div>
          <QRCodePreview productName={product.name} />
        </div>

        {/* Right: product info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          <div>
            <p style={{
              fontSize: '0.65rem',
              letterSpacing: '0.25em',
              color: COLORS.gold,
              margin: '0 0 0.75rem',
              fontWeight: 600,
            }}>
              SEASON 01 — QRQY
            </p>
            <h1 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 900,
              letterSpacing: '-0.01em',
              color: COLORS.white,
              margin: '0 0 0.5rem',
              lineHeight: 1,
            }}>
              {product.name}
            </h1>
            <p style={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: COLORS.white,
              margin: 0,
            }}>
              ${product.price}
            </p>
          </div>

          <p style={{
            fontSize: '0.92rem',
            lineHeight: 1.75,
            color: COLORS.gray,
            margin: 0,
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
          }}>
            {product.tagline}
          </p>

          <p style={{
            fontSize: '0.88rem',
            lineHeight: 1.75,
            color: COLORS.gray,
            margin: 0,
          }}>
            This piece doesn't just make a statement. It <em>is</em> the statement.
            Scan your QRQY and send followers anywhere — your drop, your playlist, your world.
            The QR code is printed using high-contrast, error-corrected ink rated for 50+ wash cycles.
          </p>

          {/* Size selector */}
          {needsSize && (
            <div>
              <p style={{
                fontSize: '0.7rem',
                letterSpacing: '0.15em',
                color: COLORS.white,
                margin: '0 0 0.75rem',
                fontWeight: 600,
              }}>
                SIZE {selectedSize ? `— ${selectedSize}` : ''}
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {SIZES.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    style={{
                      width: '48px',
                      height: '48px',
                      border: selectedSize === size
                        ? `1px solid ${COLORS.gold}`
                        : `1px solid rgba(255,255,255,0.2)`,
                      backgroundColor: selectedSize === size ? COLORS.gold : 'transparent',
                      color: selectedSize === size ? COLORS.black : COLORS.white,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      letterSpacing: '0.05em',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add to cart */}
          <button
            onClick={handleAddToCart}
            disabled={needsSize && !selectedSize}
            style={{
              padding: '1.1rem',
              backgroundColor: addedToCart ? COLORS.gold : COLORS.white,
              color: COLORS.black,
              border: 'none',
              fontSize: '0.8rem',
              fontWeight: 800,
              letterSpacing: '0.2em',
              cursor: needsSize && !selectedSize ? 'not-allowed' : 'pointer',
              opacity: needsSize && !selectedSize ? 0.4 : 1,
              transition: 'background-color 0.15s',
            }}
          >
            {addedToCart ? 'ADDED' : (needsSize && !selectedSize) ? 'SELECT A SIZE' : 'ADD TO CART'}
          </button>

          {/* Claim info block */}
          <div style={{
            borderTop: `1px solid rgba(255,255,255,0.08)`,
            paddingTop: '1.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}>
            <p style={{
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              color: COLORS.gold,
              margin: 0,
              fontWeight: 700,
            }}>
              CLAIM YOUR CODE
            </p>
            <p style={{
              fontSize: '0.85rem',
              lineHeight: 1.7,
              color: COLORS.gray,
              margin: 0,
            }}>
              Owning a QRQY means owning the link behind it. Create an account at checkout
              to activate your piece and start redirecting. It takes 30 seconds — and it's yours forever.
            </p>
            <Link to="/checkout" style={{
              fontSize: '0.72rem',
              letterSpacing: '0.12em',
              color: COLORS.white,
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            }}>
              Learn how ownership works →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
